/**
 * Transaction reducer
 */

import { ActionTypes as types, defaultCardSpec } from '../constants'
import { FUNNEL } from '../../../../lib/constants'
import serverProps from '../utils/serverProps'
import { isEmpty, get, reject } from 'lodash'
import {
  getOptionById,
  getOptionBySourceIds,
  updateFundingOption,
  getRelevantFundingSource,
  generateDisallowedSources,
  convertBankToDisallowedSource,
} from '../utils/funding-option-utils/staticUtils'
import { resetBeaconMetadata } from '../../lib/fraudNet'
import Events from 'lib/analytics/eventTags'
import { getUuid } from 'transfer/utils/uuid'
import { tracking } from 'lib/analytics/mParticle'

function getInitialState(props = serverProps) {
  const {
    // refactor this to only use the the id and legId in server.js for guest payRequest
    requestId,
    requestLegId,
    payRequestLink,
    requester = {},
    profile = {},
    cardSpecs = {},
    classicGuestUrl,
    anonymousPayment = '',
    isPersonalOrPremierRecipient,
    isPurchase,
    personalContextID = get(props, 'recipientData.personalContextID') || '',
    purchaseContextID = get(props, 'recipientData.purchaseContextID') || '',
    payRequest: { suggestedTransactionType = 'UNKNOWN' } = {},
    beaconClientMetaDataId,
    recipientData: {
      shippingAddressCollectionPreference = '',
      paymentTypeTransitions = {},
      isAnonymousPayment,
      editableFields = undefined,
    } = {},
    shippingDetails = {},
    items = [],
    shippingType = '',
    attemptId = '',
    hasEngagedContact = false,
    funnel = '',
    reUseAttemptId = false,
    hasSeenNote = false,
    dcFlow = '',
    cancel = false,
    pendingResolutions = [],
    consolidatedFlowIntent = undefined,
  } = props

  if (attemptId) {
    Events.updateCommonProps({ p2p_attempt_id: attemptId })
  }

  Events.updateCommonProps({
    flow_intent: consolidatedFlowIntent ?? 'undefined',
  })

  // get recipient data either from:
  // 1) the requester from a pay request
  // 2) the profile data from ppme
  // 3) more places???
  const recipient = {
    ...requester,
    ...profile,
    name: requester.name || profile.displayName,
  }

  // We suggest payment type based on several conditions,
  // yet we default to 'buy' in case we cannot decide for historic reason
  // Without necessarily breaking existing code, we add this variable
  // to decide whether we should default to the suggested payment type.
  // If the value is false, we will show payment type interstitial;
  // otherwise, user will see a preselected payment type
  let shouldDefaultToSuggestedPaymentType = true

  function getIsPurchase() {
    if (isPurchase !== undefined) {
      return isPurchase === 'true' || isPurchase === true
    }
    if (suggestedTransactionType === 'PERSONAL') {
      return false
    }
    if (suggestedTransactionType === 'PURCHASE') {
      return true
    }
    // if the above rules cannot decide,
    // we flag the suggested payment type to not to be defaulted
    if (isPersonalOrPremierRecipient) {
      shouldDefaultToSuggestedPaymentType = false
    }
    return true
  }

  return {
    requestId,
    requestLegId,
    payRequestLink,
    isPurchase: getIsPurchase(),
    personalContextID,
    purchaseContextID,
    paymentTypeIsSelected: isPurchase !== undefined,
    shouldDefaultToSuggestedPaymentType,
    recipient, // TODO: is this still needed (with the recipient reducer?)
    cardSpecs,
    cardSpec: defaultCardSpec, // the card spec based on the card number
    cardNumber: '',
    expiry: '',
    securityCode: '',
    // TODO should we consider making fundingOptions and selectedFundingOption undefined until we actually have one
    // it might reduce awkward checks for selectedFundingOption.id for existence (we also may have more control if we default in functions downstream)
    fundingOptions: [],
    paymentDetails: {},
    selectedFundingOption: {},
    preselectedFundingOption: {},
    billingAddressId: '',
    shippingAddressId: '',
    complianceInfo: {},
    classicGuestUrl,
    intendsToAddShippingAddress: false,
    selectedInstallmentPlan: {},
    showUnilateralFields: false,
    hasFetchedFundingOptions: false,
    isPreferredSourceDisallowed: false,
    ppCurrencyExchange: {},
    // keeping track of this separately from selectedFundingOption so that we can
    // "remember" the funding option that was chosen by the user, and re-fetches
    // (like after currency conversion selection) choose the same one the user had chosen
    fundingSourceIdsOfLastSelectedFundingOption: [],
    hasResolvedCVVContingencies: false,
    hasResolved3DSContingencies: false,
    hasResolvedDDCContingencies: false,
    hasResolved3DS2Contingencies: false,
    cvv: '',
    transactionIds: [],
    category: '',
    anonymousPayment,
    suggestedTransactionType,
    isPersonalOrPremierRecipient,
    ddcCalls: [],
    fireDDC: false,
    ddcJWT: '',
    deviceDataCollectionMode: '',
    deviceDataCollectionUrl: '',
    threeDSecureProtocol: '',
    threeDS2ReferenceId: '',
    threeDS2TransactionId: '',
    is3DS2ChallengeRequired: false,
    challengeJWT: '',
    challengeRedirectLink: '',
    challengeRedirectMethod: '',
    successfullyAddedFundingInstrumentIds: '',
    showFISelector: true,
    beaconClientMetaDataId,
    shippingAddressCollectionPreference,
    refetchedFundingOptionFor3DS: false,
    showStateMandatedWarning: false,
    paymentTypeReviewMessage: '',
    paymentReviewDisclosureMessage: '',
    items,
    shippingDetails,
    paymentTypeTransitions,
    shippingType,
    attemptId,
    hasEngagedContact,
    funnel,
    reUseAttemptId,
    hasSeenNote,
    isSenderReturningFromCIP: false,
    walletSuccess: '',
    anonymousStatus: isAnonymousPayment ? 'checked' : '',
    dcFlow,
    cancel,
    pendingResolutions,
    editableFields: editableFields,
    supportedPurposeCodes: [],
    shouldShowPOPOverlay: false,
    selectedPurposeOfPaymentOption: {},
    purposeCodeReviewDisclosureMessage: '',
    hasPurposeCodeError: false,
    consolidatedFlowIntent,
  }
}

// TODO: combine with main reducer function? Yes I think.
const fundingOptionsReducer = (state, action) => {
  switch (action.type) {
    case types.TRANSACTION.RECEIVED_FUNDING_OPTIONS: {
      const {
        selectedFundingOption = {},
        preselectedFundingOption = {},
        fundingOptions = [],
        disallowedSources = [],
        complianceInfo = {},
        riskHoldOverpanelVariant = '',
        additionalDisclosureActions = [],
        isPreferredSourceDisallowed,
        conversionType,
        payerId,
        showFISelector = true,
        showStateMandatedWarning = false,
        paymentTypeReviewMessage = '',
        paymentReviewDisclosureMessage = '',
        items = [],
        shippingDetails = {},
        shippingType = '',
        purchaseConsentPreChecked = false,
        purchaseConsentChecked = false,
        purchaseConsentLabel = '',
        purchaseConsentRequired = false,
        hasResolvedPurchaseConsent = false,
        hasResolvedPurposeCodeRequired = false,
        userAgreementConsentLabel = '',
        userAgreementConsentMinVersion = '',
        userAgreementConsentMaxVersion = '',
        userAgreementConsentRequired = false,
        hasResolvedUserAgreementConsent = false,
        purposeCodeReviewDisclosureMessage = '',
        paymentSupportedPurposeCode = [],
        paymentContingencies = [],
      } = action.data

      const transformedShippingDetails = {
        shippingFee: get(shippingDetails, 'value', '0'),
        currencyCode: get(shippingDetails, 'currencyCode', 'USD'),
        displayFee: get(shippingDetails, 'displayFee', null),
      }

      const ppCurrencyExchange = selectedFundingOption.currency_exchange
        ? selectedFundingOption.currency_exchange
        : state.ppCurrencyExchange

      return {
        ...state,
        fundingOptions,
        disallowedSources,
        riskHoldOverpanelVariant,
        additionalDisclosureActions,
        selectedFundingOption,
        preselectedFundingOption,
        ppCurrencyExchange,
        complianceInfo: {
          ...complianceInfo,
        },
        hasFetchedFundingOptions: true,
        isPreferredSourceDisallowed,
        hasResolvedCVVContingencies: false,
        hasResolved3DSContingencies: false,
        hasResolved3DS2Contingencies: false,
        is3DS2ChallengeRequired: false,
        refetchedFundingOptionFor3DS: false,
        cvv: '',
        conversionType,
        successfullyAddedFundingInstrumentIds: '',
        payerId,
        showFISelector,
        items,
        shippingDetails: transformedShippingDetails,
        showStateMandatedWarning,
        paymentTypeReviewMessage,
        paymentReviewDisclosureMessage,
        shippingType,
        purchaseConsentPreChecked,
        purchaseConsentChecked,
        purchaseConsentLabel,
        purchaseConsentRequired,
        hasResolvedPurposeCodeRequired,
        purposeCodeReviewDisclosureMessage,
        hasResolvedPurchaseConsent,
        userAgreementConsentLabel,
        userAgreementConsentMinVersion,
        userAgreementConsentMaxVersion,
        userAgreementConsentRequired,
        hasResolvedUserAgreementConsent,
        paymentSupportedPurposeCode,
        paymentContingencies,
      }
    }

    case types.TRANSACTION.RTB_ERROR: {
      const { fundingOptions } = state
      const { fundingOptionId } = action.data
      const currentFundingOption = fundingOptions.find(
        ({ id }) => id === fundingOptionId
      )
      const fundingSource = getRelevantFundingSource(currentFundingOption)
      // We are removing all `fundingOptions` that has this disabled `funding_source`
      const remainingFundingOptions = fundingOptions.filter(
        ({ funding_sources }) =>
          funding_sources.every(({ id }) => fundingSource.id !== id)
      )

      return {
        ...state,
        fundingOptions: remainingFundingOptions,
        selectedFundingOption: remainingFundingOptions[0] || {},
        disallowedSources: [
          ...state.disallowedSources,
          ...generateDisallowedSources([
            convertBankToDisallowedSource(fundingSource),
          ]),
        ],
      }
    }

    case types.TRANSACTION.RTB_INVALID_CREDENTIALS:
    case types.TRANSACTION.RESOLVED_RTB_CONTINGENCY:
      const {
        fundingOptionId,
        action: contingency,
        eCheck = {},
        // newContingency will only be defined for RTB_INVALID_CREDENTIALS when
        // resolving a REFRESH contingency throws invalid credentials error.
        newContingency,
      } = action.data
      const { fundingSourceId, ...rest } = eCheck

      // Remove contingency from funding option after resolving it.
      const updatedFundingOptions = updateFundingOption(
        state.fundingOptions,
        fundingOptionId,
        option => ({
          ...option,
          // If called with eCheck, we add eCheck data to the funding source
          funding_sources: !fundingSourceId
            ? option.funding_sources
            : option.funding_sources.map(source => {
                if (source.id === fundingSourceId) {
                  return {
                    ...source,
                    ...rest,
                  }
                }
                return source
              }),
          hasRTBContingency: !!newContingency,
          contingencies: reject(option.contingencies, {
            action: contingency,
          }).concat(newContingency ? [{ action: newContingency }] : []),
        })
      )

      return {
        ...state,
        fundingOptions: updatedFundingOptions,

        // Update currently selected funding option.
        selectedFundingOption:
          state.selectedFundingOption &&
          updatedFundingOptions.find(
            ({ id }) => id === state.selectedFundingOption.id
          ),
      }

    case types.TRANSACTION.UPDATE_PREFERRED_IN_CLIENT_DATA:
      return {
        ...state,
        fundingOptions: action.data.fundingOptions,
        selectedFundingOption: action.data.selectedFundingOption,
      }

    case types.TRANSACTION.CHANGE_SELECTED_FUNDING_OPTION:
      let newlySelectedFundingOption = {}

      // if multiple ids, check funding sources instead
      if (Array.isArray(action.data.id)) {
        newlySelectedFundingOption = getOptionBySourceIds(
          state.fundingOptions,
          action.data.id
        )
      } else {
        newlySelectedFundingOption = getOptionById(
          state.fundingOptions,
          action.data.id
        )
      }
      const {
        funding_sources = [],
        isInstallAllowed = false,
      } = newlySelectedFundingOption
      const fundingSourceIdsOfLastSelectedFundingOption = funding_sources.map(
        source => source.id
      )
      const { selectedInstallmentPlan } = state

      const newExchange = newlySelectedFundingOption.currency_exchange
        ? newlySelectedFundingOption.currency_exchange
        : state.ppCurrencyExchange

      return {
        ...state,
        selectedFundingOption: newlySelectedFundingOption,
        preselectedFundingOption: newlySelectedFundingOption,
        fundingSourceIdsOfLastSelectedFundingOption,
        ppCurrencyExchange: newExchange,
        hasResolvedCVVContingencies: false,
        hasResolved3DSContingencies: false,
        hasResolvedDDCContingencies: false,
        hasResolvedPurposeCodeRequired: false,
        cvv: '',
        selectedInstallmentPlan: isInstallAllowed
          ? selectedInstallmentPlan
          : {},
      }

    case types.TRANSACTION.CLEAR_FUNDING_OPTIONS:
      return {
        ...state,
        hasFetchedFundingOptions: false,
        fundingOptions: [],
        selectedFundingOption: {},
        hasResolvedCVVContingencies: false,
        hasResolved3DSContingencies: false,
        cvv: '',
        pendingResolutions: [],
        purposeCodeReviewDisclosureMessage: '',
      }

    case types.TRANSACTION.STORE_ALL_DDC:
      return {
        ...state,
        ddcCalls: action.data.ddcCalls,
      }

    case types.TRANSACTION.START_DDC_CALL:
      return {
        ...state,
        fireDDC: true,
        ddcJWT: action.data.jwt,
        deviceDataCollectionMode: action.data.deviceDataCollectionMode,
        deviceDataCollectionUrl: action.data.deviceDataCollectionUrl,
        threeDSecureProtocol: action.data.threeDSecureProtocol,
        threeDS2ReferenceId: action.data.threeDS2ReferenceId,
      }

    case types.TRANSACTION.END_DDC_CALL:
      const ddcCallsUpdated = state.ddcCalls
      if (!isEmpty(ddcCallsUpdated)) {
        ddcCallsUpdated.shift()
      }
      return {
        ...state,
        ddcCalls: ddcCallsUpdated,
        fireDDC: false,
        ddcJWT: '',
        deviceDataCollectionMode: '',
        deviceDataCollectionUrl: '',
        threeDSecureProtocol: '',
        threeDS2ReferenceId: '',
      }

    case types.TRANSACTION.RESOLVE_CVV:
      const { selectedFundingOption: updatedFundingOption } = state
      const { contingencies = [], show3DS = false } = action.data
      if (show3DS) {
        updatedFundingOption.show3DS = true
        updatedFundingOption.contingencies.push(...contingencies)
      }
      return {
        ...state,
        selectedFundingOption: updatedFundingOption,
        hasResolvedCVVContingencies: true,
      }

    case types.TRANSACTION.RESOLVE_PURCHASE_CONSENT:
      return {
        ...state,
        hasResolvedPurchaseConsent: true,
      }

    case types.TRANSACTION.RESOLVE_PURPOSE_CODE_CONTINGENCY:
      return {
        ...state,
        hasResolvedPurposeCodeRequired: true,
      }

    case types.TRANSACTION.RESOLVE_3DS:
      return {
        ...state,
        hasResolved3DSContingencies: true,
      }

    case types.TRANSACTION.SET_PENDING_RESOLUTIONS:
      return {
        ...state,
        pendingResolutions: action.data.pendingResolutions,
      }

    case types.TRANSACTION.SET_PAYMENT_BLOCKED:
      return {
        ...state,
        isPaymentBlocked: action.data.isPaymentBlocked,
      }

    case types.TRANSACTION.SET_CONTINGECY_FUNDING_ID:
      return {
        ...state,
        contingencyFundingId: action.data.contingencyFundingId,
      }

    case types.TRANSACTION.CHANGE_CVV_CODE:
      return {
        ...state,
        cvv: action.data.cvv,
        hasResolvedCVVContingencies: false,
      }

    case types.TRANSACTION.DDC_RESOLVED:
      return {
        ...state,
        hasResolvedDDCContingencies: true,
        is3DS2ChallengeRequired: false,
      }

    case types.TRANSACTION.THREE_DS2_SECURE_AUTH_REQUIRED:
      return {
        ...state,
        hasResolvedDDCContingencies: true,
        is3DS2ChallengeRequired: true,
        challengeJWT: action.data.jwt,
        challengeRedirectLink: action.data['3d_secure_redirect_link'],
        challengeRedirectMethod: action.data.cardinal_redirect_method,
        threeDS2ReferenceId: action.data.three_ds_reference_id,
        threeDS2TransactionId: action.data.three_ds_transaction_id,
      }

    case types.TRANSACTION.RESOLVED_3DS2_AUTH_CONTINGENCY:
      // This value is populated from the dust file and is available for env=DONATE
      let threeDS2Contingency = {}
      try {
        threeDS2Contingency = JSON.parse(
          get(action.data, 'threeDS2Contingency')
        )
      } catch (e) {
        threeDS2Contingency = {}
      }
      // Update threeDS2TransactionId to new one if it exists, if not keep previous one as it is
      const threeDS2TransactionId =
        get(threeDS2Contingency, 'three_ds_transaction_id') ||
        state.threeDS2TransactionId

      return {
        ...state,
        hasResolved3DS2Contingencies: true,
        threeDS2TransactionId,
      }

    case types.TRANSACTION.FAILED_3DS2_AUTH_CONTINGENCY:
      return {
        ...state,
        hasResolved3DS2Contingencies: false,
        hasResolvedDDCContingencies: false,
        hasFetchedFundingOptions: false,
        fundingOptions: [],
        selectedFundingOption: {},
        pendingResolutions: [],
      }

    case types.TRANSACTION.SAVE_SUCCESS_ID: {
      const successfullyAddedFundingInstrumentIds =
        action.data.successfullyAddedFundingInstrumentIds
      return {
        ...state,
        successfullyAddedFundingInstrumentIds,
      }
    }

    case types.TRANSACTION.SET_OPEN_BANKING_ERROR: {
      const { openBankingError } = action.data
      return {
        ...state,
        openBankingError,
      }
    }

    case types.TRANSACTION.UNAVAILABLE_FI: {
      const { unavailableFI, accountType } = action.data
      return {
        ...state,
        unavailableFI,
        accountType,
      }
    }

    case types.TRANSACTION.REFETCH_FUNDING_OPTIONS_3DS:
      return {
        ...state,
        refetchedFundingOptionFor3DS: true,
        hasResolved3DS2Contingencies: false,
        hasResolvedDDCContingencies: false,
        hasFetchedFundingOptions: false,
        fundingOptions: [],
        selectedFundingOption: {},
        pendingResolutions: [],
      }

    default:
      return state
  }
}

export default (state = getInitialState(), action) => {
  switch (action.type) {
    case types.EXTERNAL.RECEIVED_DATA:
      return getInitialState({ ...state, ...action.data })

    case types.TRANSACTION.CHANGE_IS_PURCHASE:
      return {
        ...state,
        isPurchase: action.data.isPurchase,
        shouldDefaultToSuggestedPaymentType:
          action.data.shouldDefaultToSuggestedPaymentType,
      }

    case types.TRANSACTION.RECEIVED_SPECS:
      return {
        ...state,
        cardSpecs: action.data.cardSpecs,
      }

    case types.TRANSACTION.PURCHASE_CONSENT_IS_CHECKED:
      return {
        ...state,
        purchaseConsentChecked: action.data.purchaseConsentChecked,
      }

    case types.TRANSACTION.CHANGE_CARD_NUMBER:
    case types.TRANSACTION.CHANGE_EXPIRY:
    case types.TRANSACTION.CHANGE_SECURITY_CODE:
    case types.TRANSACTION.CHANGE_CARD_TYPE:
    case types.TRANSACTION.RECEIVED_FUNDING_OPTIONS:
    case types.TRANSACTION.CLEAR_FUNDING_OPTIONS:
    case types.TRANSACTION.CHANGE_SELECTED_FUNDING_OPTION:
    case types.TRANSACTION.CHANGE_CVV_CODE:
    case types.TRANSACTION.RESOLVE_CVV:
    case types.TRANSACTION.RESOLVE_3DS:
    case types.TRANSACTION.STORE_ALL_DDC:
    case types.TRANSACTION.START_DDC_CALL:
    case types.TRANSACTION.END_DDC_CALL:
    case types.TRANSACTION.DDC_RESOLVED:
    case types.TRANSACTION.THREE_DS2_SECURE_AUTH_REQUIRED:
    case types.TRANSACTION.RESOLVED_3DS2_AUTH_CONTINGENCY:
    case types.TRANSACTION.FAILED_3DS2_AUTH_CONTINGENCY:
    case types.TRANSACTION.UPDATE_PREFERRED_IN_CLIENT_DATA:
    case types.TRANSACTION.RTB_ERROR:
    case types.TRANSACTION.RTB_INVALID_CREDENTIALS:
    case types.TRANSACTION.RESOLVED_RTB_CONTINGENCY:
    case types.TRANSACTION.SAVE_SUCCESS_ID:
    case types.TRANSACTION.SET_OPEN_BANKING_ERROR:
    case types.TRANSACTION.UNAVAILABLE_FI:
    case types.TRANSACTION.REFETCH_FUNDING_OPTIONS_3DS:
    case types.TRANSACTION.SET_PENDING_RESOLUTIONS:
    case types.TRANSACTION.SET_PAYMENT_BLOCKED:
    case types.TRANSACTION.SET_CONTINGECY_FUNDING_ID:
      return fundingOptionsReducer(state, action)

    case types.TRANSACTION.SENDER_RETURNING_FROM_CIP:
      return {
        ...state,
        isSenderReturningFromCIP: action.data,
      }

    case types.TRANSACTION.SUPPORTED_PURPOSE_OF_CODE:
      return {
        ...state,
        supportedPurposeCodes: action.data.supportedPurposeCodes,
        shouldShowPOPOverlay: action.data.shouldShowPOPOverlay,
        hasPurposeCodeCollectionRequired:
          action.data.hasPurposeCodeCollectionRequired,
        selectedPurposeOfPaymentOption:
          action.data.selectedPurposeOfPaymentOption,
      }

    case types.TRANSACTION.SELECTED_PURPOSE_OF_CODE:
      return {
        ...state,
        hasResolvedPurposeCodeRequired: false,
        hasPurposeCodeError: false,
        selectedPurposeOfPaymentOption:
          action.data.selectedPurposeOfPaymentOption,
      }
    case types.TRANSACTION.HAS_PURPOSE_CODE_ERROR: {
      return {
        ...state,
        hasPurposeCodeError: true,
      }
    }
    case types.USER.CREATED_FULL_ACCOUNT:
      const { address = {} } = action.data
      return {
        ...state,
        billingAddressId: address.id,
        shippingAddressId: address.id,
      }

    case types.TRANSACTION.SET_FRAUDNET_CLIENT_METADATA:
      const { beaconClientMetaDataId } = action.data
      resetBeaconMetadata(beaconClientMetaDataId)
      return {
        ...state,
      }

    case types.TRANSACTION.INTENDS_TO_ADD_SHIPPING_ADDRESS:
      return {
        ...state,
        intendsToAddShippingAddress: action.data.doesIntend,
      }

    case types.TRANSACTION.REQUESTED_MONEY:
      return {
        ...state,
        requestId: action.data.requestId,
        requestLegId: action.data.requestLegId,
        transactionIds: action.data.transactionIds,
        payRequestLink: action.data.payRequestLink,
        pStudioContent: action.data.pStudioContent,
      }

    case types.TRANSACTION.SENT_MONEY:
    case types.TRANSACTION.PAID_REQUEST:
      return {
        ...state,
        paymentDetails: action.data.paymentDetails,
        complianceInfo: {
          ...state.complianceInfo, // keep whatever may have been there
          ...action.data.complianceInfo, // plow over with what may be new
        },
      }

    case types.TRANSACTION.CHANGE_SELECTED_INSTALLMENT_PLAN:
      const {
        selectedFundingOption: { installPlan: installmentPlans = [] },
      } = state
      const selectedInstallmentPlan =
        installmentPlans.find(
          installmentPlan =>
            installmentPlan.paymentTimes === action.data.paymentTimes
        ) || {}
      return {
        ...state,
        selectedInstallmentPlan,
      }

    case types.TRANSACTION.ADDED_CARD:
      return {
        ...state,
        cardSpec: defaultCardSpec,
        cardNumber: '',
        expiry: '',
        securityCode: '',
      }

    case types.TRANSACTION.SHOW_UNILATERAL:
      return {
        ...state,
        showUnilateralFields: true,
      }

    case types.TRANSACTION.SELECT_PAYMENT_TYPE:
      return {
        ...state,
        paymentTypeIsSelected: action.data.paymentTypeIsSelected,
      }

    case types.EXTERNAL.RECEIVED_EXTERNAL_PARAMS:
    case types.CROSS_BORDER.RECEIVED_RECIPIENT_DETAILS:
      return {
        ...state,
        showUnilateralFields: false,
      }

    case types.TRANSACTION.NO_FUNDING_SOURCE:
      const { unavailableFI } = action.data
      return {
        ...state,
        hasFetchedFundingOptions: true,
        unavailableFI,
      }

    case types.TRANSACTION.FETCH_FUNDING_OPTIONS:
    case types.TRANSACTION.ADDED_CARD:
    case types.AMOUNT.CHANGE_AMOUNT:
    case types.RECIPIENT.SELECT_CONTACT:
      return {
        ...state,
        hasFetchedFundingOptions: false,
        selectedFundingOption: {},
        fundingOptions: [],
        pendingResolutions: [],
      }

    case types.TRANSACTION.LEAVING_SUCCESS_PAGE: {
      Events.updateCommonProps({ flow_intent: 'undefined' })
      return {
        ...getInitialState(),
        shippingAddressCollectionPreference: '',
        paymentTypeReviewMessage: '',
        paymentReviewDisclosureMessage: '',
        paymentTypeTransitions: {},
        attemptId: '',
        hasEngagedContact: false,
        funnel: '',
        reUseAttemptId: false,
        hasSeenNote: false,
        walletSuccess: '',
        isPaymentBlocked: false,
        cancel: false,
        pendingResolutions: [],
        purposeCodeReviewDisclosureMessage: '',
        selectedPurposeOfPaymentOption: {},
        editableFields: undefined,
        consolidatedFlowIntent: undefined,
      }
    }

    case types.TRANSACTION.CANCELLED_TRANSACTION: {
      Events.updateCommonProps({ flow_intent: 'undefined' })
      return {
        ...getInitialState(),
        shippingAddressCollectionPreference: '',
        paymentTypeReviewMessage: '',
        paymentReviewDisclosureMessage: '',
        paymentTypeTransitions: {},
        attemptId: state.attemptId, // keep attempt ID
        hasEngagedContact: state.hasEngagedContact, // keep engagement
        funnel: state.funnel, // maintain funnel
        reUseAttemptId: state.reUseAttemptId,
        hasSeenNote: false,
        walletSuccess: '',
        isPaymentBlocked: false,
        cancel: false,
        pendingResolutions: [],
        purposeCodeReviewDisclosureMessage: '',
        hasResolvedPurposeCodeRequired: false,
        selectedPurposeOfPaymentOption: {},
        editableFields: undefined,
        consolidatedFlowIntent: undefined,
      }
    }

    case types.TRANSACTION.HAS_SEEN_NOTE:
      return {
        ...state,
        hasSeenNote: true,
      }
    case types.TRANSACTION.SET_SUGGESTED_TRANSACTION_TYPE:
      return {
        ...state,
        suggestedTransactionType: action.data.transactionType,
      }
    case types.TRANSACTION.SET_SERVICE_RETURNED_PAYMENT_TYPE:
      return {
        ...state,
        serviceReturnedType: action.data.transactionType,
      }
    case types.TRANSACTION.UPDATE_GUEST_FF_ELIGIBILITY:
      return {
        ...state,
        // In the case that friends and family is no longer eligible,
        // we need to set this to false.
        isPurchase: true,
      }
    case types.TRANSACTION.GUEST_REVAMP_UPDATE_CVV: {
      return {
        ...state,
        cvv: action.data.cvv,
      }
    }
    case types.TRANSACTION.GUEST_REVAMP_UPDATE_ADDRESS_ID: {
      const addresses = action.data
      return {
        ...state,
        billingAddressId: addresses[0].addressId,
        shippingAddressId: addresses[1].addressId,
      }
    }

    case types.TRANSACTION.UPDATE_PAYMENT_TYPE_TRANSITIONS:
      return {
        ...state,
        paymentTypeTransitions: action.data.paymentTypeTransitions,
      }

    case types.TRANSACTION.RESOLVE_PURCHASE_CONSENT: {
      return {
        ...state,
        hasResolvedPurchaseConsent: true,
      }
    }

    case types.TRANSACTION.RESOLVE_PURPOSE_CODE_CONTINGENCY:
      return {
        ...state,
        hasResolvedPurposeCodeRequired: true,
      }

    case types.TRANSACTION.SET_SUGGESTED_INSTRUMENT_CONTEXT: {
      return {
        ...state,
        personalContextID: action.data.personalContextID,
        purchaseContextID: action.data.purchaseContextID,
      }
    }

    case types.TRANSACTION.FUNNEL_STARTED: {
      let attemptId
      if (
        action.data.funnel === FUNNEL.SEND ||
        action.data.funnel === FUNNEL.REQUEST ||
        action.data.funnel === FUNNEL.CONSOLIDATED
      ) {
        if (state.reUseAttemptId) {
          attemptId = state.attemptId
        } else {
          attemptId = getUuid()
        }
        Events.updateCommonProps({ p2p_attempt_id: attemptId })
        tracking(Events.p2p_flow_started())
      } else {
        attemptId = ''
        Events.updateCommonProps({ p2p_attempt_id: undefined })
      }

      return {
        ...state,
        attemptId,
        funnel: action.data.funnel,
        hasEngagedContact: false,
        reUseAttemptId: false,
      }
    }

    case types.TRANSACTION.CONTACT_ENGAGED: {
      tracking(Events.p2p_contact_engaged())

      return {
        ...state,
        hasEngagedContact: true,
      }
    }

    case types.TRANSACTION.RESOLVE_USER_AGREEMENT_CONSENT:
      return {
        ...state,
        hasResolvedUserAgreementConsent: true,
      }

    case types.TRANSACTION.UPDATE_CONSOLIDATED_FLOW_INTENT: {
      const { consolidatedFlowIntent } = action.data

      Events.updateCommonProps({ flow_intent: consolidatedFlowIntent })
      return {
        ...state,
        consolidatedFlowIntent,
      }
    }

    case types.EXTERNAL.RECEIVED_EXTERNAL_INTEGRATION_PARAMS:
      const { success } = action.data

      return {
        ...state,
        ...(success && success !== '' && { walletSuccess: success }),
      }

    case types.EXTERNAL.CHANGE_ANONYMOUS_STATUS:
      return {
        ...state,
        anonymousStatus: action.data.anonymousStatus,
      }

    default:
      return state
  }
}
